import * as React from 'react';
// react-helmet-async dropped in the React 19 migration. The BreadcrumbList
// JSON-LD is injected imperatively into <head> via a useEffect below so
// React 19's metadata-hoisting algorithm can't duplicate it across
// re-renders (same pattern as src/components/SEO/index.jsx).
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { styled } from '@mui/system';
import isCyrillic from 'helpers/isCyrillic';
import { BREADCRUMBS_LENGTH } from 'variables/main';
import { generateBreadcrumbSchema, getBaseUrl } from 'utils/seo';
import getLocale from 'helpers/getLocale';
import config from 'config';

const Wrapper = styled('nav')(({ theme }) => ({
  marginBottom: 10,
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  [theme.breakpoints.down('md')]: {
    marginTop: 20
  }
}));

const ActiveLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'colorCustom'
})(({ theme, colorCustom }) => ({
  color: colorCustom !== 'false' ? colorCustom : theme.palette.primary.main,
  cursor: 'initial',
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text'
}));

const RotateChevron = styled('span')(({ color }) => ({
  display: 'inline-block',
  transform: 'rotate(180deg)',
  marginRight: 5,
  color
}));

/**
 * Enhanced BreadCrumbs Component
 * 
 * Features:
 * - Accessible navigation
 * - JSON-LD structured data for SEO
 * - Responsive mobile view
 * - Internationalization support
 * 
 * @param {Array} breadcrumbs - Array of breadcrumb items with label, link, and optional properties
 * @param {boolean} includeSchema - Whether to include JSON-LD schema (default: true)
 * @param {boolean} mobileShowParent - On mobile, collapse to the parent crumb
 *   (second-to-last) instead of the first crumb (default: false)
 */
const BasicBreadcrumbs = ({ breadcrumbs, includeSchema = true, mobileShowParent = false }) => {
  const t = useTranslate('Breadcrumbs');
  const navigate = useNavigate();

  const isMobile = useSelector((state) => state.app.isMobile);
  const baseUrl = React.useMemo(() => getBaseUrl(), []);

  // Get site name based on brand + locale. config.brand is the source of truth
  // (baked in by the brand-specific config swap during the Docker build); fall
  // back to hostname/locale for environments where config.brand isn't set.
  const siteName = React.useMemo(() => {
    if (config.brand === 'jobalto') return 'Jobalto';
    if (config.brand === 'profitroll') return 'Профітроль';
    if (typeof window === 'undefined') return 'Jobalto';
    const hostname = window.location.hostname;
    const locale = getLocale();
    if (hostname.includes('profitroll') || locale === 'ua') {
      return 'Профітроль';
    }
    return 'Jobalto';
  }, []);

  const handleRedirect = React.useCallback((link) => navigate(link), [navigate]);

  const cutBreadcrumbsLength = React.useCallback((text = '') => {
    if (text.length > BREADCRUMBS_LENGTH) {
      return text.slice(0, BREADCRUMBS_LENGTH) + '...';
    }
    return text;
  }, []);

  // Generate structured data for breadcrumbs
  const breadcrumbSchema = React.useMemo(() => {
    if (!includeSchema || breadcrumbs.length === 0) return null;
    
    // Transform breadcrumbs for schema generation
    const schemaItems = breadcrumbs.map((crumb) => {
      let label = isCyrillic(crumb.label) || crumb.raw ? crumb.label : t(crumb.label);
      // Append site name to "Home" label in schema as well
      if (crumb.label === 'Home' || label === t('Home')) {
        label = `${label} ${siteName}`;
      }
      return {
        label,
        link: crumb.link
      };
    });
    
    return generateBreadcrumbSchema(schemaItems, baseUrl);
  }, [breadcrumbs, includeSchema, baseUrl, t, siteName]);

  // Inject the BreadcrumbList JSON-LD into <head> imperatively. Using JSX
  // here under React 19 produces duplicated <script> tags on each re-render
  // because React's metadata hoisting doesn't dedupe across commits.
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const MARK = 'data-breadcrumb-jsonld';
    document.querySelectorAll(`[${MARK}]`).forEach((el) => el.remove());
    if (!breadcrumbSchema) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute(MARK, '');
    el.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(el);
  }, [breadcrumbSchema]);

  if (breadcrumbs.length === 0) {
    return null;
  }

  // On mobile the trail collapses to a single tappable link. Default is the
  // first crumb; mobileShowParent points it at the parent (second-to-last).
  const mobileCrumb =
    (mobileShowParent && breadcrumbs[breadcrumbs.length - 2]) || breadcrumbs[0];
  const separatorColor = breadcrumbs[0]?.color;

  return (
    <>

      <Wrapper aria-label={t('Breadcrumbs')}>
        {!isMobile ? (
          <Breadcrumbs 
            aria-label={t('Breadcrumbs')} 
            separator={'▸'}
            itemScope
            itemType="https://schema.org/BreadcrumbList"
            sx={{
              userSelect: 'text',
              WebkitUserSelect: 'text',
              MozUserSelect: 'text',
              msUserSelect: 'text',
              ...(separatorColor && {
                '& .MuiBreadcrumbs-separator': { color: separatorColor }
              }),
              '& *': {
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                msUserSelect: 'text'
              }
            }}
          >
            {breadcrumbs.map((breadcrumb, index) => {
              const last = index === breadcrumbs.length - 1;

              // Get base label
              let baseLabel = isCyrillic(breadcrumb.label) || breadcrumb.raw
                ? breadcrumb.label
                : t(breadcrumb.label);
              
              // Append site name to "Home" label
              if (breadcrumb.label === 'Home' || baseLabel === t('Home')) {
                baseLabel = `${baseLabel} ${siteName}`;
              }

              const text = cutBreadcrumbsLength(baseLabel);

              return (
                <Link
                  component="button"
                  key={breadcrumb.label}
                  underline={'none'}
                  color={breadcrumb.color ? 'white' : 'textPrimary'}
                  variant="breadcrumbs"
                  tabIndex={last ? -1 : 0}
                  aria-label={text}
                  aria-current={last ? 'page' : undefined}
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                  sx={{
                    userSelect: 'text',
                    WebkitUserSelect: 'text',
                    MozUserSelect: 'text',
                    msUserSelect: 'text'
                  }}
                  onClick={() => {
                    import('services/analytics').then((Analytics) => {
                      Analytics.default.event('BreadcrumbsClick');
                    });
                    handleRedirect(breadcrumb.link, breadcrumb.callback);
                    breadcrumb.callback && breadcrumb.callback();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleRedirect(breadcrumb.link, breadcrumb.callback);
                    }
                  }}>
                  <span 
                    itemProp="name"
                    style={{
                      userSelect: 'text',
                      WebkitUserSelect: 'text',
                      MozUserSelect: 'text',
                      msUserSelect: 'text'
                    }}>
                    {last ? (
                      <>
                        {breadcrumb.color && breadcrumbs.length === 1 ? (
                          <RotateChevron color={breadcrumb.color}>{'▸'}</RotateChevron>
                        ) : null}
                        <ActiveLink colorCustom={breadcrumb.color || 'false'}>{text}</ActiveLink>
                      </>
                    ) : (
                      <span style={{ 
                        color: breadcrumbs[0].color,
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        MozUserSelect: 'text',
                        msUserSelect: 'text'
                      }}>{text}</span>
                    )}
                  </span>
                  {breadcrumb.link && (
                    <meta itemProp="item" content={`${baseUrl}${breadcrumb.link}`} />
                  )}
                  <meta itemProp="position" content={String(index + 1)} />
                </Link>
              );
            })}
          </Breadcrumbs>
        ) : (
          <Link
            component="button"
            key={mobileCrumb?.label}
            underline={'none'}
            color="textPrimary"
            variant="breadcrumbs"
            tabIndex={0}
            aria-label={mobileCrumb?.label}
            sx={{
              userSelect: 'text',
              WebkitUserSelect: 'text',
              MozUserSelect: 'text',
              msUserSelect: 'text'
            }}
            onClick={() => {
              import('services/analytics').then((Analytics) => {
                Analytics.default.event('BreadcrumbsClick');
              });
              handleRedirect(mobileCrumb?.link, mobileCrumb?.callback);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleRedirect(mobileCrumb?.link, mobileCrumb?.callback);
              }
            }}>
            {mobileCrumb.color ? (
              <>
                <RotateChevron color={mobileCrumb.color}>{'▸'}</RotateChevron>
                <span style={{
                  color: mobileCrumb.color,
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text'
                }}>
                  {(() => {
                    let label = isCyrillic(mobileCrumb?.label)
                      ? mobileCrumb?.label
                      : t(mobileCrumb?.label);
                    if (mobileCrumb?.label === 'Home' || label === t('Home')) {
                      label = `${label} ${siteName}`;
                    }
                    return label;
                  })()}
                </span>
              </>
            ) : (
              <>
                <KeyboardBackspaceIcon aria-hidden="true" />
                <span style={{
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text'
                }}>
                  {(() => {
                    let label = isCyrillic(mobileCrumb?.label)
                      ? mobileCrumb?.label
                      : t(mobileCrumb?.label);
                    if (mobileCrumb?.label === 'Home' || label === t('Home')) {
                      label = `${label} ${siteName}`;
                    }
                    return label;
                  })()}
                </span>
              </>
            )}
          </Link>
        )}
      </Wrapper>
    </>
  );
};

export default BasicBreadcrumbs;
